import { nextTick } from '@vue/composition-api';
import Cookies from 'js-cookie';
import { encode } from 'js-base64';

// 引入工具类
import i18n, { i18n_t } from '@/locales/i18n';
import { checkIsOauth2 } from '@/renderer/scripts/composition/channel';
import user, { User } from '@/renderer/scripts/utils/user';
import smh, { SMH } from '@/renderer/scripts/smh';
import store from '@/renderer/store/index';
import tips from '@/renderer/scripts/utils/tips';
import { CONSTANT_URL } from '@/renderer/scripts/http';
import storage from '@/renderer/scripts/utils/storage';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';
import { getDeviceName } from '@/renderer/scripts/utils/tools';

// TDOO 触发app扫码(暂不支持)
export const autoLogin = async (userInfo: SMH.LoginData, loginType: string, loginToPath?: string) => {
  const result: boolean = await new Promise(resolve => resolve(true));
  return result;
};

// 微信、企微扫码、yufu解析code通用
const loginWithCodeHandler = (loginCode = '', codeType: string): string => {
  // 解析过的code缓存，刷新页面后不再用code尝试登录
  const storeKeyMap: Record<string, any> = {
    wechat: storage.StoreKey.WECHAT_CODE_LIST,
    wecom: storage.StoreKey.WECOM_CODE_LIST,
    sso: storage.StoreKey.SSO_CODE_LIST,
  };
  const storeKey = storeKeyMap[codeType];
  const codeList: string[] = storage.get<string[]>(storeKey) || [];
  user.setRemember(true);
  if (codeList.includes(loginCode)) {
    const { loginAction } = user.getUserInfo();
    store.commit('setLoginingMask', false);
    if (loginAction === User.LoginActionKey.bindPhone) {
      return '';
    }
    user.setLoginAction(User.LoginActionKey.login);
    dispatchRouter(
      {
        name: 'login',
      },
      'replace',
    );
    return '';
  }
  codeList.push(loginCode);
  // 只保留5条记录
  if (codeList.length > 5) {
    codeList.shift();
    // 兜底一下 限制5条
    codeList.length = 5;
  }
  storage.set(storeKey, codeList);
  return loginCode;
};

// 微信登录
export const loginWithWechatCode = async ({ code = '', channelTag = '' }) => {
  const wechatCode = loginWithCodeHandler(code, 'wechat');
  if (!wechatCode) return;
  try {
    const deviceId = getDeviceName();
    const { loginFunc, invitationCode } = user.getUserInfo();
    const reqParams = {
      code: wechatCode,
      domain: CONSTANT_URL.currentDomain,
      deviceId,
      ...(channelTag && {
        pf: channelTag,
      }),
    };
    if (loginFunc === User.LoginFuncKey.loginToJoin) {
      Object.assign(reqParams, {
        inviteCode: invitationCode,
      });
    }
    const res = await smh.api.loginByWeChat(reqParams);
    if (res.hasError) {
      tips.error(res);
      store.commit('setLoginingMask', false);
      if (res.code === 'UserNotBindPhoneNumber') {
        // 未绑定手机号时，需要保留code绑定时用
        user.setLoginAction(User.LoginActionKey.bindPhone);
        user.setLoginBindType(User.LoginBindTypeKey.bindPhoneInWeChat);
      } else {
        // 其他报错需要清除code，因为code只能用一次
        user.setLoginAction(User.LoginActionKey.login);
        dispatchRouter(
          {
            name: 'login',
          },
          'replace',
        );
      }
      return;
    }
    store.commit('setLoginingMask', false);
    user.setLoginType(User.LoginTypeKey.loginWithWechat);
    // oauth2下只验证 不登录
    const isOauth2 = checkIsOauth2();
    user.verifyAndLogin(res, isOauth2);
  } catch (e: any) {
    tips.error(e as MsgError);
  }
};

// 企微登录
export const loginWithWecomCode = async ({ code = '', appid = '', realdomain = '' }) => {
  const wecomCode = loginWithCodeHandler(code, 'wecom');
  if (!wecomCode) return;
  try {
    const deviceId = getDeviceName();
    const { loginFunc, invitationCode } = user.getUserInfo();
    const reqParams = {
      code: wecomCode,
      corpid: appid,
      domain: realdomain,
      deviceId,
    };
    if (loginFunc === User.LoginFuncKey.loginToJoin) {
      Object.assign(reqParams, {
        inviteCode: invitationCode,
      });
    }
    const res = await smh.api.loginByWeCom(reqParams);
    if (res.hasError) {
      tips.error(res);
      store.commit('setLoginingMask', false);
      if (res.code === 'UserNotBindPhoneNumber') {
        // 未绑定手机号时，需要保留code绑定时用
        user.setLoginAction(User.LoginActionKey.bindPhone);
        user.setLoginBindType(User.LoginBindTypeKey.bindPhoneInWeCom);
        user.setWecomParams({
          corpid: appid,
          domain: realdomain,
        });
      } else {
        // 其他报错需要清除code，因为code只能用一次
        user.setLoginAction(User.LoginActionKey.login);
        dispatchRouter(
          {
            name: 'login',
          },
          'replace',
        );
      }
      return;
    }
    store.commit('setLoginingMask', false);
    user.setLoginType(User.LoginTypeKey.loginWithWecom);
    user.verifyAndLogin(res);
  } catch (e: any) {
    tips.error(e as MsgError);
  }
};

// web端使用cookie自动登录
export const loginWithCookie = async () => {
  try {
    const res = await smh.userAPI.getUserOrg();
    const lastLoginedOrg = res.data.find(item => item.isLastSignedIn) || res.data[0];
    const editionConfig = lastLoginedOrg?.extensionData?.editionConfig;
    const editionFlag = editionConfig?.editionFlag || 'personal';
    const orgChannel = lastLoginedOrg?.extensionData?.channelFlag;
    user.setLoginType(User.LoginTypeKey.loginWithCookie);
    await user.setOrgAndLogin({
      organizationId: lastLoginedOrg.id,
      libraryId: lastLoginedOrg.libraryId,
      multiOrg: res.data.length > 1,
      expired: lastLoginedOrg.expired,
      editionConfig,
      editionFlag,
      orgChannel,
    });
    store.commit('setLoginingMask', false);
  } catch (e) {
    user.signOut();
    tips.error(i18n_t('获取登录信息失败，请重新登录'));
    setTimeout(() => {
      window.location.reload();
    }, 200);
  }
};

// web端使用cookie恢复oauth2登录态（主要取出userId）
export const loginOauth2WithCookie = () => {
  const { userId, isNewUser, isOrgNewUser, userToken, isLogined, userRole, hasAppliedChannelOrg } =
    user.getUserInfoSync();
  user.verifyAndLogin(
    {
      userId,
      isNewUser,
      isOrgNewUser,
      userToken,
      isLogined,
      userRole,
      hasAppliedChannelOrg,
    },
    true,
  );
  store.commit('setLoginingMask', false);
};
export const loginVistorWithCookie = () => {
  // 自动登录结果
  let loginResult = false;
  const { pathname } = window.location;
  let vistorUserToken;
  if (pathname.includes('/web/collect') || pathname.includes('/web/share')) {
    vistorUserToken = Cookies.get('VISTOR_USER_TOKEN');
  } else {
    return loginResult;
  }
  if (vistorUserToken) {
    const vistorPhoneNumber = Cookies.get('VISTOR_PHONE_NUMBER');
    if (vistorPhoneNumber) {
      store.commit('SET_VISTOR_PHONE_NUMBER', vistorPhoneNumber);
      store.commit('SET_VISTOR_LOGINED', true);
      loginResult = true;
    } else {
      Cookies.remove('VISTOR_USER_TOKEN');
    }
  } else {
    Cookies.remove('VISTOR_PHONE_NUMBER');
  }
  return loginResult;
};

// yufu sso登录
export const loginWithSSOCode = async ({ tenantKey = '', code = '', type = '' }) => {
  try {
    const ssoCode = loginWithCodeHandler(code, 'sso');
    if (!ssoCode) return;
    const deviceId = getDeviceName();
    const { loginFunc, invitationCode } = user.getUserInfo();
    const reqParams = {
      tenantKey,
      deviceId,
      type,
      code: ssoCode,
      domain: CONSTANT_URL.currentDomain,
    };
    if (loginFunc === User.LoginFuncKey.loginToJoin) {
      Object.assign(reqParams, {
        inviteCode: invitationCode,
      });
    }
    const res = await smh.api.loginByYufu(reqParams);
    if (res.hasError) {
      tips.error(res);
      store.commit('setLoginingMask', false);
      if (res.code === 'UserNotBindPhoneNumber') {
        // 未绑定手机号时，需要保留code绑定时用
        user.setLoginAction(User.LoginActionKey.bindPhone);
        user.setLoginBindType(User.LoginBindTypeKey.bindPhoneInYufu);
      } else {
        // 其他报错需要清除code，因为code只能用一次
        user.setLoginAction(User.LoginActionKey.login);
        dispatchRouter({ name: 'login' }, 'replace');
      }
      return;
    }
    store.commit('setLoginingMask', false);
    user.setLoginType(User.LoginTypeKey.loginWithYufu);
    user.verifyAndLogin(res);
  } catch (e) {
    tips.error(e as MsgError);
  }
};

// 新 sso/AD 登录
export const verifyAccountLogin = async ({
  type = '',
  orgDomain = '',
  ldapType = '',
  credential = '',
  accountName = '',
  accountPassword = '',
}) => {
  try {
    const deviceId = getDeviceName();
    const { loginFunc, invitationCode } = user.getUserInfo();
    const reqParams = {
      type,
      orgDomain,
      deviceId,
      // 有orgDomain就不传domain
      ...(!orgDomain && { domain: CONSTANT_URL.currentDomain }),
      ...(credential && { credential }),
      ...(ldapType && { ldapType }),
      ...(accountName && { accountName }),
      ...(accountPassword && { accountPassword }),
    };
    if (loginFunc === User.LoginFuncKey.loginToJoin) {
      Object.assign(reqParams, { inviteCode: invitationCode });
    }
    user.setRemember(true);
    const res = await smh.api.verifyAccountLogin(reqParams);
    // 登录完成后置空ssoFrom
    if (type === 'sso') {
      user.setSSOFrom('');
    }
    if (res.hasError) {
      tips.error(res);
      store.commit('setLoginingMask', false);
      if (res.code === 'UserNotBindPhoneNumber') {
        // 未绑定手机号时，需要保留code绑定时用
        const bindType = type === 'sso' ? User.LoginBindTypeKey.bindPhoneInSSO : User.LoginBindTypeKey.bindPhoneInLDAP;
        // 暂存ldap/ad的账密 绑定手机号时用到
        if (type === 'account-password') {
          user.setLdapAccount({
            ldapType,
            accountName,
            accountPassword,
          });
        } else if (type === 'sso') {
          user.setSSOAccount({ credential });
        }
        user.setLoginAction(User.LoginActionKey.bindPhone);
        user.setLoginBindType(bindType);
      } else {
        // 其他报错的sso登录需要清除code，因为code只能用一次
        if (type === 'sso') {
          user.setLoginAction(User.LoginActionKey.login);
          dispatchRouter({ name: 'login' }, 'replace');
        }
      }
      return;
    }
    store.commit('setLoginingMask', false);
    user.setLoginType(User.LoginTypeKey.loginWithSSO);
    user.verifyAndLogin(res);
  } catch (e) {
    tips.error(e as MsgError);
  }
};

// 账密登录
export const loginWithPassword = async ({
  accountName = '',
  password = '',
  deviceId = '',
  domain = '',
  corpId = '',
  pf = '',
}) => {
  try {
    const reqParams = {
      accountName,
      password: encode(password),
      deviceId,
      ...(domain && {
        domain,
      }),
      ...(corpId && {
        corpId,
      }),
      pf,
    };
    const res = await smh.api.loginByPassword(reqParams);
    store.commit('setLoginingMask', false);
    if (res.hasError) {
      tips.error(res);
      return;
    }
    user.verifyAndLogin(res);
  } catch (e) {
    tips.error(e as MsgError);
  }
};

// 选择企业列表即登录
export const chooseOrgLogin = (orgInfo: SMH.OrgItem) => {
  const { id, libraryId, expired, orgUser } = orgInfo;
  const editionConfig = orgInfo?.extensionData?.editionConfig;
  const editionFlag = editionConfig?.editionFlag || 'enterprise';
  const orgChannel = orgInfo?.extensionData?.channelFlag;
  user.setOrgAndLogin({
    organizationId: id,
    libraryId,
    expired,
    editionFlag,
    editionConfig,
    orgChannel,
  });
  user.setNeedChangePassword(orgUser?.needChangePassword || false);
};
